import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, addDoc, onSnapshot, query, doc, updateDoc, deleteDoc, orderBy, serverTimestamp, setDoc } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import Modal from 'react-modal';
import { FaPlus, FaEdit, FaTrash, FaShoppingCart, FaStore, FaSave, FaTimes } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import CreatableSelect from 'react-select/creatable';
import { ClipLoader } from 'react-spinners';
import useDebounce from './hooks/useDebounce';

// --- ESTILOS KOKIMIKO COMPARTIDOS ---
const formatCurrency = (value) => {
    const number = Number(value);
    if (isNaN(number)) return '0,00 €';
    return number.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const cardStyle = "bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100";
const inputStyle = "w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-kokimiko-teal focus:outline-none text-sm font-bold text-kokimiko-texto";
const labelStyle = "block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2";

const customModalStyles = {
    content: {
        top: '50%', left: '50%', right: 'auto', bottom: 'auto',
        marginRight: '-50%', transform: 'translate(-50%, -50%)',
        border: 'none', background: 'transparent', width: '100%', maxWidth: '800px', padding: '0'
    },
    overlay: { backgroundColor: 'rgba(35, 37, 89, 0.75)', zIndex: 1000 }
};

Modal.setAppElement('#root');

// --- FORMULARIO DUMMY-PROOF ---
function GastoForm({ onSave, onCancel, initialData, proveedoresOptions }) {
    const [proveedor, setProveedor] = useState(null);
    const [fecha, setFecha] = useState(new Date());
    const [currentItem, setCurrentItem] = useState({ descripcion: '', cantidad: 1, costoUnitario: 0 });
    const [items, setItems] = useState([]);

    useEffect(() => {
        if (initialData) {
            setProveedor(initialData.proveedor ? { label: initialData.proveedor, value: initialData.proveedor } : null);
            setFecha(initialData.fecha?.toDate ? initialData.fecha.toDate() : new Date(initialData.fecha || Date.now()));
            setItems(initialData.items || []);
        } else {
            setProveedor(null); 
            setFecha(new Date()); 
            setItems([]); 
            setCurrentItem({ descripcion: '', cantidad: 1, costoUnitario: 0 });
        }
    }, [initialData]);

    const handleAddItem = () => {
        if (!currentItem.descripcion.trim() || currentItem.cantidad <= 0 || currentItem.costoUnitario < 0) {
            return toast.error("Descripción, cantidad y costo son obligatorios.");
        }
        const newItem = { 
            producto: currentItem.descripcion, 
            cantidad: currentItem.cantidad, 
            costoUnitario: currentItem.costoUnitario,
            costoTotalItem: currentItem.cantidad * currentItem.costoUnitario
        };
        setItems([...items, newItem]);
        setCurrentItem({ descripcion: '', cantidad: 1, costoUnitario: 0 });
    };

    const handleRemoveItem = (indexToRemove) => {
        setItems(items.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!proveedor || items.length === 0) return toast.error("Selecciona un proveedor y añade al menos un artículo.");
        
        const dataToSave = {
            proveedor: proveedor.value,
            fecha, 
            items,
            costoTotal: items.reduce((total, item) => total + (item.costoTotalItem || 0), 0),
            estado: 'Recibido' // Simplificado: Todo gasto registrado se asume ejecutado
        };
        onSave(dataToSave, initialData?.id);
    };

    const selectStyles = { 
        control: (base) => ({ ...base, minHeight: '44px', borderRadius: '0.75rem', borderColor: '#e5e7eb', backgroundColor: '#f9fafb', fontSize: '14px', fontWeight: 'bold' }),
        menuPortal: base => ({ ...base, zIndex: 9999 }) 
    };

    const costoTotalCompra = items.reduce((total, item) => total + (item.costoTotalItem || 0), 0);

    return (
        <form onSubmit={handleSubmit} className="bg-white p-8 rounded-[2rem] shadow-2xl max-h-[90vh] overflow-y-auto font-sans">
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                <h3 className="text-2xl font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-3">
                    <div className="p-3 bg-kokimiko-teal rounded-xl text-white"><FaShoppingCart size={18} /></div>
                    {initialData?.id ? 'Editar Compra' : 'Registrar Gasto / Compra'}
                </h3>
                <button type="button" onClick={onCancel} className="text-gray-400 hover:text-red-500 transition-colors p-2"><FaTimes size={24}/></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                    <label className={labelStyle}>Proveedor / Comercio</label>
                    <CreatableSelect 
                        isClearable 
                        options={proveedoresOptions} 
                        value={proveedor} 
                        onChange={setProveedor} 
                        placeholder="Ej: Amazon, Mercadona..." 
                        formatCreateLabel={userInput => `Crear proveedor: "${userInput}"`} 
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                    />
                </div>
                <div>
                    <label className={labelStyle}>Fecha de Compra</label>
                    <DatePicker 
                        selected={fecha} 
                        onChange={setFecha} 
                        dateFormat="dd/MM/yyyy" 
                        className={inputStyle} 
                        wrapperClassName="w-full"
                    />
                </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 mb-6">
                <h4 className="text-xs font-black text-kokimiko-texto uppercase tracking-widest mb-4">Añadir Conceptos</h4>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-6">
                        <label className={labelStyle}>Descripción del Gasto</label>
                        <input type="text" value={currentItem.descripcion} onChange={e => setCurrentItem({...currentItem, descripcion: e.target.value})} placeholder="Ej: Globos dorados, Torta..." className={inputStyle} />
                    </div>
                    <div className="md:col-span-2">
                        <label className={labelStyle}>Cant.</label>
                        <input type="number" value={currentItem.cantidad} onChange={e => setCurrentItem({...currentItem, cantidad: Number(e.target.value)})} min="1" className={`${inputStyle} text-center`} />
                    </div>
                    <div className="md:col-span-3">
                        <label className={labelStyle}>Precio Unitario</label>
                        <input type="number" value={currentItem.costoUnitario} onChange={e => setCurrentItem({...currentItem, costoUnitario: Number(e.target.value)})} step="0.01" min="0" className={`${inputStyle} text-right`} placeholder="0.00" />
                    </div>
                    <div className="md:col-span-1">
                        <button type="button" onClick={handleAddItem} className="w-full bg-kokimiko-texto text-white font-bold h-[44px] rounded-xl hover:bg-kokimiko-teal transition-colors flex items-center justify-center shadow-md">
                            <FaPlus />
                        </button>
                    </div>
                </div>
            </div>

            <div className="mb-6">
                <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 pl-2">Artículos en esta factura</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar pr-2">
                    {items.length > 0 ? items.map((item, index) => (
                        <div key={index} className="flex justify-between items-center bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                            <div>
                                <p className="font-bold text-sm text-kokimiko-texto">{item.producto}</p>
                                <p className="text-xs text-gray-400 mt-1">{item.cantidad} x {formatCurrency(item.costoUnitario)}</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <p className="font-black text-kokimiko-teal">{formatCurrency(item.costoTotalItem)}</p>
                                <button type="button" onClick={() => handleRemoveItem(index)} className="text-red-300 hover:text-red-500 p-2 transition-colors bg-red-50 rounded-lg"><FaTrash size={12}/></button>
                            </div>
                        </div>
                    )) : <p className="text-center text-gray-400 text-xs py-4 uppercase tracking-widest font-bold">La factura está vacía</p>}
                </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-between items-center pt-6 border-t border-gray-100 gap-4">
                <button type="button" onClick={onCancel} className="w-full sm:w-auto bg-gray-100 text-gray-500 font-black text-xs uppercase tracking-widest h-12 px-8 rounded-xl hover:bg-gray-200 transition-colors">Cancelar</button>
                <div className="flex items-center gap-6 w-full sm:w-auto">
                    <div className="text-right">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Factura:</p>
                        <p className="text-2xl font-black text-kokimiko-texto">{formatCurrency(costoTotalCompra)}</p>
                    </div>
                    <button type="submit" className="flex-1 sm:flex-none bg-kokimiko-teal text-white font-black text-xs uppercase tracking-widest h-12 px-8 rounded-xl shadow-md hover:opacity-80 transition-opacity flex items-center justify-center gap-2"><FaSave size={16}/> Guardar</button>
                </div>
            </div>
        </form>
    );
}

// --- VISTA PRINCIPAL ---
function ComprasView() {
    const [compras, setCompras] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const busquedaDebounced = useDebounce(busqueda, 300);
    
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [compraActual, setCompraActual] = useState(null);
    const [proveedores, setProveedores] = useState([]);

    useEffect(() => {
        setCargando(true);
        const comprasQuery = query(collection(db, 'compras'), orderBy('fecha', 'desc'));
        const unsubscribeCompras = onSnapshot(comprasQuery, (snapshot) => {
            setCompras(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setCargando(false);
        });

        const proveedoresQuery = query(collection(db, 'proveedores'), orderBy('name'));
        const unsubscribeProveedores = onSnapshot(proveedoresQuery, (snapshot) => {
            setProveedores(snapshot.docs.map(doc => ({ value: doc.data().name, label: doc.data().name })));
        });

        return () => { unsubscribeCompras(); unsubscribeProveedores(); };
    }, []);

    const comprasFiltradas = useMemo(() => {
        if (!busquedaDebounced) return compras;
        return compras.filter(c => 
            c.proveedor?.toLowerCase().includes(busquedaDebounced.toLowerCase()) ||
            c.items?.some(i => i.producto?.toLowerCase().includes(busquedaDebounced.toLowerCase()))
        );
    }, [compras, busquedaDebounced]);

    const handleSave = async (formData, compraId) => {
        const toastId = toast.loading('Guardando gasto...');
        try {
            // Guardar proveedor nuevo si no existe
            const proveedorExists = proveedores.some(p => p.value === formData.proveedor);
            if (!proveedorExists) {
                await setDoc(doc(db, 'proveedores', formData.proveedor), { name: formData.proveedor, createdAt: serverTimestamp() });
            }

            if (compraId) {
                await updateDoc(doc(db, 'compras', compraId), formData);
                toast.success("¡Gasto actualizado!", { id: toastId });
            } else {
                await addDoc(collection(db, 'compras'), { ...formData, createdAt: serverTimestamp() });
                toast.success("¡Gasto registrado!", { id: toastId });
            }
            closeModal();
        } catch (error) { 
            toast.error(`Error: ${error.message}`, { id: toastId }); 
        }
    };

    const handleDelete = async (compraId) => {
        if (window.confirm("¿Seguro que deseas eliminar este registro de compra?")) {
            const toastId = toast.loading('Eliminando...');
            try {
                await deleteDoc(doc(db, 'compras', compraId));
                toast.success("Registro eliminado.", { id: toastId });
            } catch (error) { 
                toast.error(`Error al eliminar`, { id: toastId }); 
            }
        }
    };

    const openModal = (compra = null) => { setCompraActual(compra); setModalIsOpen(true); };
    const closeModal = () => { setCompraActual(null); setModalIsOpen(false); };

    return (
        <div className="h-full flex flex-col p-4 md:p-8 gap-6 bg-kokimiko-fondo/30 w-full overflow-x-hidden font-sans">
            <Toaster position="top-center" />
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 gap-4">
                <h1 className="text-2xl md:text-3xl font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-3">
                    <div className="p-3 bg-kokimiko-naranja rounded-xl text-white"><FaStore /></div>
                    Registro de Compras
                </h1>
                <button onClick={() => openModal()} className="w-full md:w-auto px-6 py-3 text-sm font-black rounded-xl text-white bg-kokimiko-texto hover:bg-kokimiko-teal transition-colors flex items-center justify-center gap-2 shadow-md uppercase tracking-widest">
                    <FaPlus /> Nuevo Gasto
                </button>
            </div>

            <div className={`${cardStyle} flex-1 overflow-hidden flex flex-col`}>
                <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                    <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest pl-2">Historial de Operaciones</h3>
                    <input
                        type="text"
                        placeholder="Buscar proveedor o artículo..."
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        className={`${inputStyle} w-full sm:w-72 h-10 text-xs`}
                    />
                </div>

                {cargando ? (
                    <div className="flex-1 flex flex-col justify-center items-center">
                        <ClipLoader color="#1BB1A5" size={40} />
                        <p className="text-gray-400 mt-4 text-xs font-bold uppercase tracking-widest">Cargando Historial...</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto custom-scrollbar flex-1">
                        <table className="w-full text-sm text-left">
                            <thead className="text-[10px] text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100 font-black">
                                <tr>
                                    <th scope="col" className="px-6 py-4 rounded-tl-xl">Fecha</th>
                                    <th scope="col" className="px-6 py-4">Proveedor</th>
                                    <th scope="col" className="px-6 py-4">Artículos</th>
                                    <th scope="col" className="px-6 py-4 text-right">Total</th>
                                    <th scope="col" className="px-6 py-4 text-center rounded-tr-xl">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comprasFiltradas.length > 0 ? (
                                    comprasFiltradas.map((compra) => (
                                        <tr key={compra.id} className="border-b border-gray-50 hover:bg-kokimiko-naranja/5 transition-colors">
                                            <td className="px-6 py-4 font-bold text-gray-500 whitespace-nowrap">
                                                {compra.fecha?.toDate ? compra.fecha.toDate().toLocaleDateString('es-ES') : new Date(compra.fecha).toLocaleDateString('es-ES')}
                                            </td>
                                            <td className="px-6 py-4 font-black text-kokimiko-texto">
                                                {compra.proveedor}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    {compra.items?.slice(0, 2).map((i, idx) => (
                                                        <span key={idx} className="text-xs text-gray-500 font-semibold bg-gray-100 px-2 py-1 rounded-md inline-block max-w-max">
                                                            {i.cantidad}x {i.producto}
                                                        </span>
                                                    ))}
                                                    {compra.items?.length > 2 && <span className="text-[10px] font-bold text-gray-400">+{compra.items.length - 2} más...</span>}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right font-black text-kokimiko-texto text-base">
                                                {formatCurrency(compra.costoTotal)}
                                            </td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex justify-center items-center gap-2">
                                                    <button onClick={() => openModal(compra)} className="p-2 text-gray-400 hover:text-kokimiko-azul hover:bg-kokimiko-azul/10 rounded-lg transition-colors" title="Editar">
                                                        <FaEdit size={14} />
                                                    </button>
                                                    <button onClick={() => handleDelete(compra.id)} className="p-2 text-gray-400 hover:text-white hover:bg-red-500 rounded-lg transition-colors" title="Eliminar">
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-12 text-gray-400 font-bold text-xs uppercase tracking-widest">
                                            No hay registros de compras
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Modal isOpen={modalIsOpen} onRequestClose={closeModal} style={customModalStyles}>
                <GastoForm onSave={handleSave} onCancel={closeModal} initialData={compraActual} proveedoresOptions={proveedores} />
            </Modal>
        </div>
    );
}

export default ComprasView;